import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { KycDocumentRequirement } from '#src/__generated__/globalTypes.ts';
import { FormControlLabel } from '#src/atoms/inputs/FormControlLabel/index.tsx';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { RadioGroup } from '#src/atoms/inputs/RadioGroup/index.tsx';
import { Block } from '#src/atoms/layout/Block/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import {
  BodyM,
  HeadlineL,
  HeadlineM,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { StepWrapperFromProps } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import { MANGOPAY_PRIVACY_POLICY } from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useIntlCountries } from '#src/hooks/useIntlCountries.ts';
import { glossary } from '#src/lib/glossary.ts';

import { kycDocumentTypeLabels } from './messages.ts';

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  > * {
    width: 100%;
  }
`;

const LinkInFormattedMessage = styled.a`
  text-decoration: underline;
`;

type Props = {
  issuingCountry?: { value: string; label: string };
  setIssuingCountry: (country: { value: string; label: string }) => void;
  kycDocumentRequirements: KycDocumentRequirement[];
  kycDocumentRequirementsLoading: boolean;
  documentRequirement?: KycDocumentRequirement;
  setDocumentRequirement: (type: KycDocumentRequirement) => void;
  onBack: () => void;
  onNext: () => void;
};

export const ChooseDocument = ({
  issuingCountry,
  setIssuingCountry,
  kycDocumentRequirements,
  kycDocumentRequirementsLoading,
  documentRequirement,
  onBack,
  onNext,
  setDocumentRequirement,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const countries = useIntlCountries(true);

  const documentRequirementsOptions = Object.values(
    kycDocumentRequirements
  ).map(kycDocumentRequirement => {
    return {
      value: kycDocumentRequirement.documentType,
      label: (
        <BodyM color="var(--c-white)" bold>
          {formatMessage(
            kycDocumentTypeLabels[kycDocumentRequirement.documentType]
          )}
        </BodyM>
      ),
    };
  });

  return (
    <StyledGraphqlForm
      onSuccess={() => {
        onNext();
      }}
      onSubmit={(values, onResult) => {
        onResult({});
      }}
      render={(Error, SubmitButton) => (
        <StepWrapperFromProps
          onBack={onBack}
          submitButton={
            <SubmitButton size="medium" disabled={!documentRequirement}>
              <FormattedMessage {...glossary.next} />
            </SubmitButton>
          }
        >
          <HeadlineL as="h3">
            <FormattedMessage
              id="createFiatWallet.chooseId.title"
              defaultMessage="Add your ID"
            />
          </HeadlineL>
          <LabelM>
            <FormattedMessage
              id="createFiatWallet.chooseId.description"
              defaultMessage="Please provide a government-issued ID to enable cash deposits, withdrawals, and rewards."
            />
          </LabelM>
          <HeadlineM as="h4">
            <FormattedMessage
              id="createFiatWallet.chooseId.subtitle"
              defaultMessage="Choose an ID type to add"
            />
          </HeadlineM>
          <StyledFormControlLabel
            label={
              <BodyM bold color="var(--c-white)">
                <FormattedMessage
                  id="ChooseDocument.issuer"
                  defaultMessage="Issuing country/region"
                />
              </BodyM>
            }
            control={
              <NativeSelect
                placeholder={formatMessage({
                  id: 'createFiatWallet.tellUsAboutYou.issuer.placeholder',
                  defaultMessage: 'Select an issuing country/region',
                })}
                fullWidth
                name="country"
                value={issuingCountry?.value ?? ''}
                values={countries}
                onChange={value => {
                  setIssuingCountry(countries.find(c => c.value === value)!);
                }}
              />
            }
            labelPlacement="top"
          />
          {kycDocumentRequirementsLoading ? (
            <LoadingIndicator small />
          ) : (
            <>
              {!!kycDocumentRequirements.length && (
                <StyledFormControlLabel
                  label={null}
                  control={
                    <RadioGroup
                      options={documentRequirementsOptions}
                      value={documentRequirement?.documentType}
                      name="currency"
                      modal
                      border
                      withSpacing
                      onChange={(value: string) => {
                        const selectedDocumentRequirement =
                          kycDocumentRequirements?.find(
                            requirement => requirement.documentType === value
                          );

                        if (selectedDocumentRequirement) {
                          setDocumentRequirement(selectedDocumentRequirement);
                        }
                      }}
                    />
                  }
                  labelPlacement="top"
                />
              )}
            </>
          )}
          <div>
            <Block>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage
                  id="createFiatWallet.uploadId.privacy"
                  defaultMessage="Your ID remains private and will be handled according to Mangopay’s <link>Privacy policy</link>."
                  values={{
                    link: (...text) => (
                      <LinkInFormattedMessage
                        target="_blank"
                        href={MANGOPAY_PRIVACY_POLICY}
                      >
                        {text}
                      </LinkInFormattedMessage>
                    ),
                  }}
                />
              </LabelM>
            </Block>
          </div>
        </StepWrapperFromProps>
      )}
    />
  );
};
